import Config from "@config";

// The lambda itself times out at 150 s (test) / 180 s (prod); giving the
// browser slightly less makes a hung request fail here, with a message,
// before it fails there.
const GENERATION_TIMEOUT_MS = 170 * 1000;

/**
 * Error thrown by the AI server client. Carries the HTTP status and the
 * lambda's JSON body (`{ status, message, errors? }`) when the server
 * answered, or `code: "ECONNABORTED"` when the request timed out. The
 * axios-shaped `response` alias is kept so callers can read
 * `error.response.status` either way.
 */
export class AiServerError extends Error {
    constructor(message, { status = null, data = null, code = null } = {}) {
        super(message);
        this.name = "AiServerError";
        this.status = status;
        this.data = data;
        this.code = code;
        this.response = status ? { status, data } : undefined;
    }
}

function endpoint(path) {
    const base = String(Config.aiServer || "").replace(/\/+$/, "");
    return `${base}/${path.replace(/^\/+/, "")}`;
}

// The same bearer AuthContext.configApi hands axios for core-platform.
function bearer() {
    const token = localStorage.getItem("token");
    return token ? `Bearer ${token}` : "";
}

/**
 * POST /content/video-script on platform-builder-server.
 *
 * Deliberately `fetch`, not the app's axios: AuthContext loads axios with
 * defaults meant for core-platform — an `X-Requested-With` header and a
 * stray `withCredentials` *header* (`axios.defaults.headers.withCredentials`)
 * — and the lambda's CORS allow-list refuses both at preflight. Cookies must
 * not travel either, because the lambda answers
 * `Access-Control-Allow-Origin: *`. Sending exactly these three headers with
 * credentials omitted keeps the request inside what the lambda allows.
 *
 * @param {{ contentId: string, prompt: string, duration: number, personality?: string }} payload
 *        `personality` is one of the server's ContentVideoScriptDTO keys
 *        (didactic | casual | motivational | technical | storyteller)
 * @param {{ signal?: AbortSignal }} [options]  caller's abort signal (closing the modal)
 * @returns {Promise<{ script: object, context: object }>}
 * @throws {AiServerError|DOMException} AiServerError on HTTP errors and timeouts;
 *         the caller's own AbortError when `signal` aborts
 */
export async function generateContentVideoScript({ contentId, prompt, duration, personality }, { signal } = {}) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, GENERATION_TIMEOUT_MS);

    const forwardAbort = () => controller.abort();
    if (signal?.aborted) forwardAbort();
    else signal?.addEventListener("abort", forwardAbort, { once: true });

    let response;
    try {
        response = await fetch(endpoint("content/video-script"), {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: bearer(),
            },
            body: JSON.stringify({ contentId, prompt, duration, ...(personality ? { personality } : {}) }),
            signal: controller.signal,
        });
    } catch (e) {
        if (timedOut) {
            throw new AiServerError("The AI server did not answer in time", { code: "ECONNABORTED" });
        }
        throw e;
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", forwardAbort);
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
        throw new AiServerError(data?.message || `AI server answered ${response.status}`, {
            status: response.status,
            data,
        });
    }

    return data;
}
